import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, Languages, Sun, ArrowRight } from "lucide-react";
import { STRINGS } from "@/lib/i18n";
import { HERO_IMAGE } from "@/lib/images";
import Bubble from "@/components/Bubble";
import TypingDots from "@/components/TypingDots";
import LosCabosLogo from "@/components/LosCabosLogo";
import useCaboChat from "@/hooks/useCaboChat";

function readInitialLang() {
  if (typeof window === "undefined") return "en";
  const params = new URLSearchParams(window.location.search);
  return params.get("lang") === "es" ? "es" : "en";
}

function readEmbedFlag() {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return params.get("embed") === "1";
}

/* ---------------- Shared chat subcomponents ---------------- */

function WelcomeBubble({ t, size = "default" }) {
  const titleCls =
    size === "compact"
      ? "font-display text-lg leading-snug mb-1.5"
      : "font-display text-lg md:text-xl leading-snug mb-1.5";
  const bodyCls =
    size === "compact"
      ? "text-[14px] leading-relaxed text-[#3a4867]"
      : "text-[14.5px] leading-relaxed text-[#3a4867]";
  const ctaCls =
    size === "compact"
      ? "mt-2 text-[12.5px] font-medium text-[#faa200] flex items-center gap-1"
      : "mt-2 text-[13px] font-medium text-[#faa200] flex items-center gap-1";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-2.5"
    >
      <div className="mt-1 h-9 w-9 rounded-lg bg-[#faa200] flex items-center justify-center text-white shadow-sm shrink-0">
        <Sun size={18} strokeWidth={2.2} />
      </div>
      <div className="bg-[#f7f1e7] border border-[#eee5d1] rounded-br-2xl rounded-tr-2xl rounded-tl-2xl rounded-bl-md px-4 py-3.5 max-w-[85%] text-[#242f44]">
        <div className={titleCls}>{t.welcomeTitle}</div>
        <div className={bodyCls}>{t.welcomeBody}</div>
        <div className={ctaCls}>
          {t.welcomeCta} <ArrowRight size={14} />
        </div>
      </div>
    </motion.div>
  );
}

function TypingBubble() {
  return (
    <div className="flex gap-2.5" data-testid="typing-wrapper">
      <div className="mt-1 h-9 w-9 rounded-lg bg-[#faa200] flex items-center justify-center text-white shadow-sm shrink-0">
        <Sun size={18} strokeWidth={2.2} />
      </div>
      <div className="bg-[#f7f1e7] border border-[#eee5d1] rounded-2xl">
        <TypingDots />
      </div>
    </div>
  );
}

function SuggestionChips({ suggestions, onSend, streaming, compact }) {
  const cls = compact
    ? "shrink-0 px-3.5 py-1.5 rounded-full text-[12px] font-medium bg-white text-[#242f44] border border-[#242f44]/15 hover:bg-[#242f44] hover:text-white hover:border-[#242f44] disabled:opacity-50 transition-colors"
    : "shrink-0 px-4 py-2 rounded-full text-[13px] font-medium bg-white text-[#242f44] border border-[#242f44]/15 hover:bg-[#242f44] hover:text-white hover:border-[#242f44] disabled:opacity-50 transition-colors";
  return (
    <div className="chips-scroll flex gap-2 overflow-x-auto pb-1" data-testid="suggestion-chips">
      {suggestions.map((s, i) => (
        <button
          key={`chip-${s}`}
          type="button"
          onClick={() => onSend(s)}
          disabled={streaming}
          className={cls}
          data-testid={`suggestion-chip-${i}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function ChatInputForm({
  input,
  setInput,
  onSubmit,
  streaming,
  placeholder,
  sendLabel,
  inputRef,
  compact,
}) {
  const inputCls = compact
    ? "flex-1 px-4 py-2.5 rounded-full bg-[#f7f9fc] border border-[#e4e9f0] focus:border-[#faa200] focus:bg-white outline-none text-[#242f44] placeholder:text-[#7a879e]/80 disabled:opacity-70 text-[14px] transition-colors"
    : "flex-1 px-4 py-3 rounded-full bg-[#f7f9fc] border border-[#e4e9f0] focus:border-[#faa200] focus:bg-white outline-none text-[#242f44] placeholder:text-[#7a879e]/80 disabled:opacity-70 transition-colors";
  const btnCls = compact
    ? "h-11 w-11 rounded-full bg-[#faa200] hover:bg-[#e69200] text-white flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-transform active:scale-95"
    : "h-12 w-12 rounded-full bg-[#faa200] hover:bg-[#e69200] text-white flex items-center justify-center shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-transform active:scale-95";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className={`px-4 ${compact ? "" : "md:px-6"} py-3 bg-white border-t border-[#e4e9f0] flex items-center gap-2 ${compact ? "shrink-0" : ""}`}
      data-testid="chat-input-form"
    >
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={placeholder}
        disabled={streaming}
        className={inputCls}
        data-testid="chat-input"
        aria-label="Chat input"
      />
      <button
        type="submit"
        disabled={streaming || !input.trim()}
        className={btnCls}
        data-testid="send-button"
        aria-label={sendLabel}
      >
        <Send size={18} />
      </button>
    </form>
  );
}

function ChatFeed({ scrollRef, messages, streaming, t, showWelcome, size }) {
  const lastIsAssistant =
    messages.length > 0 &&
    messages[messages.length - 1].role === "assistant" &&
    !messages[messages.length - 1].content;

  const padCls =
    size === "compact"
      ? "chat-scroll flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-white"
      : "chat-scroll flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-4 bg-white";

  return (
    <div ref={scrollRef} className={padCls} data-testid="chat-feed">
      {showWelcome && <WelcomeBubble t={t} size={size} />}
      <AnimatePresence initial={false}>
        {messages.map((m) => (
          <Bubble key={m.id} msg={m} />
        ))}
      </AnimatePresence>
      {streaming && lastIsAssistant && <TypingBubble />}
    </div>
  );
}

/* ---------------- Embed mode ---------------- */

function EmbedView({ chat, t, scrollRef, inputRef }) {
  const { messages, input, setInput, streaming, sendMessage, toggleLang } = chat;
  const showWelcome = messages.length === 0;

  return (
    <div
      className="min-h-screen w-full font-body bg-transparent"
      data-testid="cabo-concierge-embed"
    >
      <div
        className="w-full h-screen flex flex-col bg-white overflow-hidden border border-[#e4e9f0]"
        data-testid="chat-widget"
      >
        <div className="relative h-32 md:h-36 overflow-hidden shrink-0">
          <div
            className="absolute inset-0 bg-cover bg-center ken-burns"
            style={{ backgroundImage: `url(${HERO_IMAGE})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#242f44] via-[#242f44]/55 to-[#242f44]/10" />
          <div className="relative z-10 h-full flex items-end justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#faa200] flex items-center justify-center text-white shadow-md pulse-orange">
                <Sun size={18} strokeWidth={2.3} />
              </div>
              <div className="text-white">
                <div className="font-display text-lg md:text-xl leading-tight">
                  {t.widgetTitle}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-white/90 text-[11px] md:text-xs">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#faa200]" />
                  {t.online} · {t.widgetSubtitle}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleLang}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-xs font-medium hover:bg-white/25"
              data-testid="lang-toggle"
            >
              <Languages size={12} />
              {t.langLabel}
            </button>
          </div>
        </div>

        <ChatFeed
          scrollRef={scrollRef}
          messages={messages}
          streaming={streaming}
          t={t}
          showWelcome={showWelcome}
          size="compact"
        />

        <div className="px-4 pt-2 pb-1.5 bg-white border-t border-[#e4e9f0] shrink-0">
          <SuggestionChips
            suggestions={t.suggestions}
            onSend={sendMessage}
            streaming={streaming}
            compact
          />
        </div>

        <ChatInputForm
          input={input}
          setInput={setInput}
          onSubmit={sendMessage}
          streaming={streaming}
          placeholder={t.inputPlaceholder}
          sendLabel={t.send}
          inputRef={inputRef}
          compact
        />
      </div>
    </div>
  );
}

/* ---------------- Full page view ---------------- */

function HeroStats() {
  const stats = [
    "19,000+ guest rooms",
    "500+ direct weekly flights",
    "0% VAT on international events",
  ];
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      {stats.map((s) => (
        <div
          key={s}
          className="flex items-center gap-2 text-[13px] text-[#242f44]/80 bg-[#f7f1e7] border border-[#eee5d1] rounded-full px-3.5 py-1.5"
        >
          <Sparkles size={14} className="text-[#faa200]" />
          {s}
        </div>
      ))}
    </div>
  );
}

function TopBar({ t, toggleLang }) {
  return (
    <header className="relative z-20 bg-[#242f44] text-white">
      <div className="mx-auto max-w-6xl px-4 md:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3" data-testid="brand-logo">
          <LosCabosLogo className="h-8 md:h-9 w-auto" inverted />
          <span className="hidden md:inline-block h-6 w-px bg-white/20 mx-1" />
          <span className="hidden md:inline text-[11px] uppercase tracking-[0.22em] text-white/80">
            Meetings &amp; MICE
          </span>
        </div>
        <button
          type="button"
          onClick={toggleLang}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 border border-white/15 text-white text-sm font-medium hover:bg-white/20 transition-colors"
          data-testid="lang-toggle"
          aria-label="Toggle language"
        >
          <Languages size={14} />
          {t.langLabel}
        </button>
      </div>
    </header>
  );
}

function WidgetHero({ t }) {
  return (
    <div className="relative h-44 md:h-56 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center ken-burns"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#242f44] via-[#242f44]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#242f44]/60 to-transparent" />
      <div className="relative z-10 h-full flex items-end p-5 md:p-6">
        <div className="flex items-center gap-3 w-full">
          <div className="h-11 w-11 rounded-xl bg-[#faa200] flex items-center justify-center text-white shadow-lg pulse-orange">
            <Sun size={22} strokeWidth={2.3} />
          </div>
          <div className="text-white flex-1">
            <div className="font-display text-2xl md:text-[26px] leading-tight">
              {t.widgetTitle}
            </div>
            <div className="mt-1 flex items-center gap-2 text-white/90 text-xs md:text-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#faa200] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#faa200]" />
              </span>
              {t.online} · {t.widgetSubtitle}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FullPageView({ chat, t, scrollRef, inputRef }) {
  const { messages, input, setInput, streaming, sendMessage, toggleLang } = chat;
  const showWelcome = messages.length === 0;

  return (
    <div
      className="min-h-screen w-full font-body relative overflow-hidden bg-white"
      data-testid="cabo-concierge-page"
    >
      <TopBar t={t} toggleLang={toggleLang} />

      <div className="relative">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#faa200] opacity-[0.08] blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-[#242f44] opacity-[0.05] blur-3xl" />

        <div className="mx-auto max-w-6xl px-4 md:px-8 py-8 md:py-14 grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-[#faa200] text-[11px] font-semibold tracking-[0.22em] uppercase">
              <span className="h-1.5 w-1.5 rounded-full bg-[#faa200]" />
              {t.eyebrow}
            </div>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#242f44] mt-3 leading-[1.03]"
              data-testid="page-title"
            >
              {t.pageTitle}
            </h1>
            <p className="mt-5 text-[#4c5b76] text-base leading-relaxed max-w-xl">
              {t.pageIntro}
            </p>
            <HeroStats />
            <div className="mt-8 text-[12px] text-[#7a879e]">{t.disclaimer}</div>
          </div>

          <div className="lg:col-span-7">
            <div
              className="w-full h-[80vh] lg:h-[720px] flex flex-col bg-white rounded-[24px] overflow-hidden border border-[#e4e9f0]"
              style={{ boxShadow: "0 32px 80px rgba(36, 47, 68, 0.14)" }}
              data-testid="chat-widget"
            >
              <WidgetHero t={t} />
              <ChatFeed
                scrollRef={scrollRef}
                messages={messages}
                streaming={streaming}
                t={t}
                showWelcome={showWelcome}
              />

              <div className="px-4 md:px-6 pt-3 pb-2 bg-white border-t border-[#e4e9f0]">
                <SuggestionChips
                  suggestions={t.suggestions}
                  onSend={sendMessage}
                  streaming={streaming}
                />
              </div>

              <ChatInputForm
                input={input}
                setInput={setInput}
                onSubmit={sendMessage}
                streaming={streaming}
                placeholder={t.inputPlaceholder}
                sendLabel={t.send}
                inputRef={inputRef}
              />

              <div className="text-center text-[11px] text-[#7a879e] py-2 bg-white border-t border-[#e4e9f0]">
                {t.poweredBy}
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-[#e4e9f0] bg-white">
        <div className="mx-auto max-w-6xl px-4 md:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-[#7a879e]">
          <div className="flex items-center gap-2">
            <LosCabosLogo className="h-6 w-auto opacity-80" />
            <span className="uppercase tracking-[0.2em]">Visit Los Cabos</span>
          </div>
          <div>
            © {new Date().getFullYear()} Los Cabos Tourism Board · Meetings &amp; MICE
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------------- Root ---------------- */

export default function CaboConcierge() {
  const embed = readEmbedFlag();
  const initialLang = readInitialLang();
  const chat = useCaboChat(initialLang);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Autoscroll chat feed to bottom whenever messages change or streaming state flips.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [chat.messages, chat.streaming, scrollRef]);

  // Return focus to the input when a response finishes streaming.
  useEffect(() => {
    if (!chat.streaming) {
      inputRef.current?.focus();
    }
  }, [chat.streaming]);

  const t = STRINGS[chat.lang];

  if (embed) {
    return (
      <EmbedView chat={chat} t={t} scrollRef={scrollRef} inputRef={inputRef} />
    );
  }
  return (
    <FullPageView chat={chat} t={t} scrollRef={scrollRef} inputRef={inputRef} />
  );
}
